import Cookies from "js-cookie";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { DEFAULT_AGENCY_CODE } from "src/config";
import { NotLandingPaths } from "src/navigation";
import {
  GET_TOKEN_BODY,
  GET_TOKEN_HEADER,
  useGetDossier,
  useGetDossierConfiguration,
  useGetIdToken,
} from "src/networking/terminals";
import { regexAgencyCode } from "src/regex";
import { atomDossier, atomDossierConfig, useRootLoaders } from "src/storage";
import { PrivateRouteProps, typedMemo } from "src/types";
import { Error404 } from "src/ui/pages";
import { useBoolean } from "src/utils";

export const PrivateRoute = typedMemo(
  ({ element }: PrivateRouteProps): JSX.Element => {
    const { pathname } = useLocation();

    const agencyCode =
      pathname === "/" ? DEFAULT_AGENCY_CODE : pathname.replace("/", "");

    const isNotLanding = NotLandingPaths.includes(pathname);

    const [enabled, allow] = useBoolean();

    const { someRootPending } = useRootLoaders();

    const saveDossier = useSetRecoilState(atomDossier);
    const saveDossierConfig = useSetRecoilState(atomDossierConfig);
    const { crossGate: getIdToken, pending: pendingIdToken } = useGetIdToken();
    const { crossGate: getDossier, pending: pendingGetDossier } =
      useGetDossier();
    const { crossGate: getDossierConfig, pending: pendingGetDossierConfig } =
      useGetDossierConfiguration();

    useEffect(() => {
      const init = async () => {
        if (!Cookies.get("IdToken")) {
          try {
            await requestToken();
          } catch (error) {
            console.error("Error during retrieving the token:", error);
          }
        }

        if (isNotLanding) {
          allow();
        } else if (agencyCode?.match(regexAgencyCode)) {
          requestDossier(agencyCode);
        }
      };

      init();
    }, []);

    const requestDossier = (agencyCode: string) => {
      getDossier(
        { headers: { AgenziaId: agencyCode.toUpperCase() } },
        {
          onSuccess: (dossier) => {
            saveDossier(dossier);
            getDossierConfig(
              {},
              {
                onSuccess: (dossierConfig) => {
                  saveDossierConfig(dossierConfig);
                  allow();
                },
              }
            );
          },
        }
      );
    };

    const requestToken = async () => {
      await getIdToken(
        {
          headers: GET_TOKEN_HEADER,
          data: GET_TOKEN_BODY,
        },
        {
          onSuccess: (response) => {
            if (
              response?.AuthenticationResult?.TokenType &&
              response?.AuthenticationResult?.IdToken
            ) {
              Cookies.set(
                "IdToken",
                `${response?.AuthenticationResult?.TokenType} ${response?.AuthenticationResult?.IdToken}`
              );
            }
          },
        }
      );
    };

    if (someRootPending) {
      return <></>;
    }

    return enabled ? (
      element
    ) : pendingGetDossier || pendingIdToken || pendingGetDossierConfig ? (
      <></>
    ) : (
      <Error404 />
    );
  }
);
